"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.entitlementsIsOpenBankingStandalone = exports.entitlementsIsPaymentsOnlyStandalone = exports.entitlementsMayUseErp = exports.entitlementsMayUseBankAccounts = exports.entitlementsMayUseOpenBanking = exports.entitlementsMayUseInternationalOb = exports.entitlementsMayUseFenaOb = exports.entitlementsInclude = exports.resolveCompanyEntitlementsFromSubjects = exports.subjectGrantsEntitlements = exports.capabilitiesForSubscriptionOption = exports.emptyCompanyEntitlements = exports.ENTITLEMENT_GRANTING_STATUSES = exports.CompanyCapability = void 0;
const company_subscription_types_1 = require("./company-subscription-types");
/**
 * Product capabilities for multi-subscription entitlements.
 * Gates (API, nav, modules) should check these — not a single currentSubscription.
 * @see toolkit-backend/docs/billing/multi-sub.md
 */
var CompanyCapability;
(function (CompanyCapability) {
    CompanyCapability["ERP"] = "erp";
    CompanyCapability["PAYMENTS_RYFT_CARDS"] = "payments.ryft_cards";
    CompanyCapability["PAYMENTS_CASH_OTHER"] = "payments.cash_other";
    CompanyCapability["PAYMENTS_FENA_OB"] = "payments.fena_ob";
    CompanyCapability["PAYMENTS_INTERNATIONAL_OB"] = "payments.international_ob";
    CompanyCapability["B2B_SHOPIFY"] = "b2b.shopify";
    CompanyCapability["REPORTS"] = "reports";
    CompanyCapability["WALLETS"] = "wallets";
})(CompanyCapability = exports.CompanyCapability || (exports.CompanyCapability = {}));
/** Subscription statuses that grant product capabilities (access still open). */
exports.ENTITLEMENT_GRANTING_STATUSES = [
    company_subscription_types_1.SubscriptionStatusEnum.active,
    company_subscription_types_1.SubscriptionStatusEnum.trialing,
    company_subscription_types_1.SubscriptionStatusEnum.past_due,
    company_subscription_types_1.SubscriptionStatusEnum.unpaid,
    company_subscription_types_1.SubscriptionStatusEnum.payment_action_required,
    company_subscription_types_1.SubscriptionStatusEnum.payment_failed,
    company_subscription_types_1.SubscriptionStatusEnum.paused,
];
function emptyCompanyEntitlements() {
    return { capabilities: [], grantingOptions: [] };
}
exports.emptyCompanyEntitlements = emptyCompanyEntitlements;
const TOOLKIT_CAPS = [
    CompanyCapability.ERP,
    CompanyCapability.PAYMENTS_RYFT_CARDS,
    CompanyCapability.PAYMENTS_CASH_OTHER,
    CompanyCapability.REPORTS,
    CompanyCapability.WALLETS,
];
const PAYMENTS_ONLY_CAPS = [
    CompanyCapability.PAYMENTS_FENA_OB,
    CompanyCapability.REPORTS,
    CompanyCapability.WALLETS,
];
const PAYMENTS_ONLY_EU_CAPS = [
    CompanyCapability.PAYMENTS_INTERNATIONAL_OB,
    CompanyCapability.REPORTS,
    CompanyCapability.WALLETS,
];
const B2B_CAPS = [
    CompanyCapability.B2B_SHOPIFY,
    CompanyCapability.REPORTS,
    CompanyCapability.WALLETS,
];
/** Capabilities granted by a single subscription option (when that subject is entitlement-granting). */
function capabilitiesForSubscriptionOption(option) {
    switch (option) {
        case company_subscription_types_1.SubscriptionOptionEnum.TOOLKIT:
        case company_subscription_types_1.SubscriptionOptionEnum.TOOLKIT_MOBILE:
            return [...TOOLKIT_CAPS];
        case company_subscription_types_1.SubscriptionOptionEnum.PAYMENTS_ONLY:
            return [...PAYMENTS_ONLY_CAPS];
        case company_subscription_types_1.SubscriptionOptionEnum.PAYMENTS_ONLY_EU:
            return [...PAYMENTS_ONLY_EU_CAPS];
        case company_subscription_types_1.SubscriptionOptionEnum.SHOPIFY_B2B_SUBSCRIPTION:
            return [...B2B_CAPS];
        default:
            return [];
    }
}
exports.capabilitiesForSubscriptionOption = capabilitiesForSubscriptionOption;
function subjectGrantsEntitlements(subject) {
    if (subject.onTrial)
        return true;
    const status = subject.status;
    if (!status)
        return false;
    return exports.ENTITLEMENT_GRANTING_STATUSES.includes(status);
}
exports.subjectGrantsEntitlements = subjectGrantsEntitlements;
/**
 * Pure union of capabilities from subscription subjects.
 * Does not hit the DB — apps load subjects then call this.
 */
function resolveCompanyEntitlementsFromSubjects(subjects) {
    const capSet = new Set();
    const optionSet = new Set();
    for (const subject of subjects || []) {
        if (!subjectGrantsEntitlements(subject))
            continue;
        const option = subject.subscriptionOption;
        if (!option)
            continue;
        const caps = capabilitiesForSubscriptionOption(option);
        if (!caps.length)
            continue;
        for (const cap of caps)
            capSet.add(cap);
        if (Object.values(company_subscription_types_1.SubscriptionOptionEnum).includes(option)) {
            optionSet.add(option);
        }
    }
    return {
        capabilities: Array.from(capSet),
        grantingOptions: Array.from(optionSet),
    };
}
exports.resolveCompanyEntitlementsFromSubjects = resolveCompanyEntitlementsFromSubjects;
function entitlementsInclude(entitlements, capability) {
    return entitlements.capabilities.includes(capability);
}
exports.entitlementsInclude = entitlementsInclude;
/** Fena OB product cluster: OB payments, recurring/VRP/bulk, API keys, terminals. */
function entitlementsMayUseFenaOb(entitlements) {
    return entitlementsInclude(entitlements, CompanyCapability.PAYMENTS_FENA_OB);
}
exports.entitlementsMayUseFenaOb = entitlementsMayUseFenaOb;
/** EU Open Banking product cluster: international-currency OB payments, API keys, terminals. */
function entitlementsMayUseInternationalOb(entitlements) {
    return entitlementsInclude(entitlements, CompanyCapability.PAYMENTS_INTERNATIONAL_OB);
}
exports.entitlementsMayUseInternationalOb = entitlementsMayUseInternationalOb;
/**
 * Either Open Banking product. UK (Fena OB) and EU are independent
 * subscriptions that share the OB pages; each still gates its own currencies.
 */
function entitlementsMayUseOpenBanking(entitlements) {
    return entitlementsMayUseFenaOb(entitlements) || entitlementsMayUseInternationalOb(entitlements);
}
exports.entitlementsMayUseOpenBanking = entitlementsMayUseOpenBanking;
/** Bank accounts settings: OB beneficiary and/or wallet-linked accounts. */
function entitlementsMayUseBankAccounts(entitlements) {
    return (entitlementsInclude(entitlements, CompanyCapability.PAYMENTS_FENA_OB) ||
        entitlementsInclude(entitlements, CompanyCapability.WALLETS));
}
exports.entitlementsMayUseBankAccounts = entitlementsMayUseBankAccounts;
function entitlementsMayUseErp(entitlements) {
    return entitlementsInclude(entitlements, CompanyCapability.ERP);
}
exports.entitlementsMayUseErp = entitlementsMayUseErp;
/** PO without ERP (no Toolkit stack): standalone Payments Only access mode. */
function entitlementsIsPaymentsOnlyStandalone(entitlements) {
    return entitlementsMayUseFenaOb(entitlements) && !entitlementsMayUseErp(entitlements);
}
exports.entitlementsIsPaymentsOnlyStandalone = entitlementsIsPaymentsOnlyStandalone;
/** Any OB product (UK and/or EU) without ERP: standalone payments access mode. */
function entitlementsIsOpenBankingStandalone(entitlements) {
    return entitlementsMayUseOpenBanking(entitlements) && !entitlementsMayUseErp(entitlements);
}
exports.entitlementsIsOpenBankingStandalone = entitlementsIsOpenBankingStandalone;
